import { contains, bisort, isEmpty, grep, indexOf, isUndefined } from '../utils/functions.js';
import '../math/Graph.js';
import { Point, Rect } from '../math/Shapes.js';
import { Size } from '../math/Size.js';
import { DEG_TO_RAD, EPSILON } from '../constants.js';

class TreeLayoutProcessor {
    constructor(options) {
        this.center = null;
        this.options = options;
    }
    layout(treeGraph, root) {
        this.graph = treeGraph;
        if (!this.graph.nodes || this.graph.nodes.length === 0) {
            return;
        }
        if (!contains(this.graph.nodes, root)) {
            throw new Error('The given root is not in the graph.');
        }
        this.center = root;
        this.graph.cacheRelationships();
        /* var nonull = this.graph.nodes.where(function (n) {
         return n.associatedShape != null;
         });*/
        // transfer the rects
        /* nonull.forEach(function (n) {
         n.Location = n.associatedShape.Position;
         n.NodeSize = n.associatedShape.ActualBounds.ToSize();
         }

         );*/
        // caching the children
        /* nonull.forEach(function (n) {
         n.children = n.getChildren();
         });*/
        this.layoutSwitch();
        // apply the layout to the actual visuals
        // nonull.ForEach(n => n.associatedShape.Position = n.Location);
    }
    layoutLeft(left) {
        this.setChildrenDirection(this.center, 'Left', false);
        this.setChildrenLayout(this.center, 'Default', false);
        let h = 0, w = 0, y, i, node;
        for (i = 0; i < left.length; i++) {
            node = left[i];
            node.TreeDirection = 'Left';
            const s = this.measure(node, Size.Empty.bind(this));
            w = Math.max(w, s.width);
            h += s.height + this.options.verticalSeparation;
        }
        h -= this.options.verticalSeparation;
        const x = this.center.x - this.options.horizontalSeparation;
        y = this.center.y + ((this.center.height - h) / 2);
        for (i = 0; i < left.length; i++) {
            node = left[i];
            const p = new Point(x - node.Size.width, y);
            this.arrange(node, p);
            y += node.Size.height + this.options.verticalSeparation;
        }
    }
    layoutRight(right) {
        this.setChildrenDirection(this.center, 'Right', false);
        this.setChildrenLayout(this.center, 'Default', false);
        let h = 0, w = 0, y, i, node;
        for (i = 0; i < right.length; i++) {
            node = right[i];
            node.TreeDirection = 'Right';
            const s = this.measure(node, Size.Empty.bind(this));
            w = Math.max(w, s.width);
            h += s.height + this.options.verticalSeparation;
        }
        h -= this.options.verticalSeparation;
        const x = this.center.x + this.options.horizontalSeparation + this.center.width;
        y = this.center.y + ((this.center.height - h) / 2);
        for (i = 0; i < right.length; i++) {
            node = right[i];
            const p = new Point(x, y);
            this.arrange(node, p);
            y += node.Size.height + this.options.verticalSeparation;
        }
    }
    layoutUp(up) {
        this.setChildrenDirection(this.center, 'Up', false);
        this.setChildrenLayout(this.center, 'Default', false);
        let w = 0, y, node, i;
        for (i = 0; i < up.length; i++) {
            node = up[i];
            node.TreeDirection = 'Up';
            const s = this.measure(node, Size.Empty.bind(this));
            w += s.width + this.options.horizontalSeparation;
        }
        w -= this.options.horizontalSeparation;
        let x = this.center.x + (this.center.width / 2) - (w / 2);
        // y = this.center.y -verticalSeparation -this.center.height/2 - h;
        for (i = 0; i < up.length; i++) {
            node = up[i];
            y = this.center.y - this.options.verticalSeparation - node.Size.height;
            const p = new Point(x, y);
            this.arrange(node, p);
            x += node.Size.width + this.options.horizontalSeparation;
        }
    }
    layoutDown(down) {
        let node, i;
        this.setChildrenDirection(this.center, 'Down', false);
        this.setChildrenLayout(this.center, 'Default', false);
        let w = 0;
        for (i = 0; i < down.length; i++) {
            node = down[i];
            node.treeDirection = 'Down';
            const s = this.measure(node, Size.Empty.bind(this));
            w += s.width + this.options.horizontalSeparation;
        }
        w -= this.options.horizontalSeparation;
        let x = this.center.x + (this.center.width / 2) - (w / 2);
        const y = this.center.y + this.options.verticalSeparation + this.center.height;
        for (i = 0; i < down.length; i++) {
            node = down[i];
            const p = new Point(x, y);
            this.arrange(node, p);
            x += node.Size.width + this.options.horizontalSeparation;
        }
    }
    layoutRadialTree() {
        // var rmax = children.Aggregate(0D, (current, node) => Math.max(node.SectorAngle, current));
        this.setChildrenDirection(this.center, 'Radial', false);
        this.setChildrenLayout(this.center, 'Default', false);
        this.previousRoot = null;
        const startAngle = this.options.startRadialAngle * DEG_TO_RAD;
        const endAngle = this.options.endRadialAngle * DEG_TO_RAD;
        if (endAngle <= startAngle) {
            throw new Error('Final angle should not be less than the start angle.');
        }
        this.maxDepth = 0;
        this.origin = new Point(this.center.x, this.center.y);
        this.calculateAngularWidth(this.center, 0);
        // perform the layout
        if (this.maxDepth > 0) {
            this.radialLayout(this.center, this.options.radialFirstLevelSeparation, startAngle, endAngle);
        }
        // update properties of the root node
        this.center.Angle = endAngle - startAngle;
    }
    tipOverTree(down, startFromLevel) {
        if (isUndefined(startFromLevel)) {
            startFromLevel = 0;
        }
        this.setChildrenDirection(this.center, 'Down', false);
        this.setChildrenLayout(this.center, 'Default', false);
        this.setChildrenLayout(this.center, 'Underneath', false, startFromLevel);
        let w = 0, node, i;
        for (i = 0; i < down.length; i++) {
            node = down[i];
            // if (node.IsSpecial) continue;
            node.TreeDirection = 'Down';
            const s = this.measure(node, Size.Empty.bind(this));
            w += s.width + this.options.horizontalSeparation;
        }
        w -= this.options.horizontalSeparation;
        // putting the root in the center with respect to the whole diagram is not a nice result, let's put it with respect to the first level only
        w -= down[down.length - 1].width;
        w += down[down.length - 1].associatedShape.bounds().width;
        let x = this.center.x + (this.center.width / 2) - (w / 2);
        const y = this.center.y + this.options.verticalSeparation + this.center.height;
        for (i = 0; i < down.length; i++) {
            node = down[i];
            // if (node.IsSpecial) continue;
            const p = new Point(x, y);
            this.arrange(node, p);
            x += node.Size.width + this.options.horizontalSeparation;
        }
        /*/ /let's place the special node, assuming there is only one
         if (down.Count(n => n.IsSpecial) > 0)
         {
         var special = (from n in down where n.IsSpecial select n).First();
         if (special.Children.Count > 0)
         throw new DiagramException("The 'special' element should not have children.");
         special.Data.Location = new Point(Center.Data.Location.X + Center.AssociatedShape.BoundingRectangle.Width + this.options.HorizontalSeparation, Center.Data.Location.Y);
         }*/
    }
    calculateAngularWidth(n, d) {
        if (d > this.maxDepth) {
            this.maxDepth = d;
        }
        const w = 1000, h = 1000, diameter = d === 0 ? 0 : Math.sqrt((w * w) + (h * h)) / d;
        let aw = 0;
        if (n.children.length > 0) {
            // eventually with n.IsExpanded
            for (let i = 0, len = n.children.length; i < len; i++) {
                const child = n.children[i];
                aw += this.calculateAngularWidth(child, d + 1);
            }
            aw = Math.max(diameter, aw);
        }
        else {
            aw = diameter;
        }
        n.sectorAngle = aw;
        return aw;
    }
    sortChildren(n) {
        let basevalue = 0, i;
        // update basevalue angle for node ordering
        if (n.parents.length > 1) {
            throw new Error('Node is not part of a tree.');
        }
        const p = n.parents[0];
        if (p) {
            const pl = new Point(p.x, p.y);
            const nl = new Point(n.x, n.y);
            basevalue = this.normalizeAngle(Math.atan2(pl.y - nl.y, pl.x - nl.x));
        }
        const count = n.children.length;
        if (count === 0) {
            return null;
        }
        const angle = [];
        const idx = [];
        for (i = 0; i < count; ++i) {
            const c = n.children[i];
            const l = new Point(c.x, c.y);
            idx[i] = i;
            angle[i] = this.normalizeAngle(-basevalue + Math.atan2(l.y - l.y, l.x - l.x));
        }
        bisort(angle, idx);
        const col = []; // list of nodes
        const children = n.children;
        for (i = 0; i < count; ++i) {
            col.push(children[idx[i]]);
        }
        return col;
    }
    normalizeAngle(angle) {
        while (angle > Math.PI * 2) {
            angle -= 2 * Math.PI;
        }
        while (angle < 0) {
            angle += Math.PI * 2;
        }
        return angle;
    }
    radialLayout(node, radius, startAngle, endAngle) {
        const deltaTheta = endAngle - startAngle;
        const deltaThetaHalf = deltaTheta / 2.0;
        const parentSector = node.sectorAngle;
        let fraction = 0;
        const sorted = this.sortChildren(node);
        for (let i = 0, len = sorted.length; i < len; i++) {
            const childNode = sorted[i];
            const cp = childNode;
            const childAngleFraction = cp.sectorAngle / parentSector;
            if (childNode.children.length > 0) {
                this.radialLayout(childNode, radius + this.options.radialSeparation, startAngle + (fraction * deltaTheta), startAngle + ((fraction + childAngleFraction) * deltaTheta));
            }
            this.setPolarLocation(childNode, radius, startAngle + (fraction * deltaTheta) + (childAngleFraction * deltaThetaHalf));
            cp.angle = childAngleFraction * deltaTheta;
            fraction += childAngleFraction;
        }
    }
    setPolarLocation(node, radius, angle) {
        node.x = this.origin.x + (radius * Math.cos(angle));
        node.y = this.origin.y + (radius * Math.sin(angle));
        node.BoundingRectangle = new Rect(node.x, node.y, node.width, node.height);
    }
    /**
     * Sets the children direction recursively.
     *
     * @param node
     * @param direction
     * @param includeStart
     */
    setChildrenDirection(node, direction, includeStart) {
        const rootDirection = node.treeDirection;
        this.graph.depthFirstTraversal(node, (n) => {
            n.treeDirection = direction;
        });
        if (!includeStart) {
            node.treeDirection = rootDirection;
        }
    }
    /**
     * Sets the children layout recursively.
     *
     * @param node
     * @param layout
     * @param includeStart
     * @param startFromLevel
     */
    setChildrenLayout(node, layout, includeStart, startFromLevel) {
        if (isUndefined(startFromLevel)) {
            startFromLevel = 0;
        }
        const rootLayout = node.childrenLayout;
        if (startFromLevel > 0) {
            // assign levels to the Node.Level property
            this.graph.assignLevels(node);
            // assign the layout on the condition that the level is at least the 'startFromLevel'
            this.graph.depthFirstTraversal(node, (s) => {
                if (s.level >= startFromLevel + 1) {
                    s.childrenLayout = layout;
                }
            });
        }
        else {
            this.graph.depthFirstTraversal(node, (s) => {
                s.childrenLayout = layout;
            });
            // if the start should not be affected we put the state back
            if (!includeStart) {
                node.childrenLayout = rootLayout;
            }
        }
    }
    /**
     * Returns the actual size of the node. The given size is the allowed space wherein the node can lay out itself.
     *
     * @param node
     * @param givenSize
     * @returns {Size}
     */
    measure(node, givenSize) {
        let w = 0, h = 0, s;
        let result = new Size(0, 0);
        if (!node) {
            throw new Error('Node is not defined.');
        }
        const b = node.associatedShape.bounds();
        const shapeWidth = b.width;
        const shapeHeight = b.height;
        if (node.parents.length !== 1) {
            throw new Error('Node not in a spanning tree.');
        }
        const parent = node.parents[0];
        if (node.treeDirection === 'Undefined') {
            node.treeDirection = parent.treeDirection;
        }
        if (isEmpty(node.children)) {
            result = new Size(Math.abs(shapeWidth) < EPSILON ? 50 : shapeWidth, Math.abs(shapeHeight) < EPSILON ? 25 : shapeHeight);
        }
        else if (node.children.length === 1) {
            switch (node.treeDirection) {
                case 'Radial':
                    s = this.measure(node.children[0], givenSize); // child size
                    w = shapeWidth + (this.options.radialSeparation * Math.cos(node.AngleToParent)) + s.width;
                    h = shapeHeight + Math.abs(this.options.radialSeparation * Math.sin(node.AngleToParent)) + s.height;
                    break;
                case 'Left':
                case 'Right':
                    switch (node.childrenLayout) {
                        case 'TopAlignedWithParent':
                            break;
                        case 'BottomAlignedWithParent':
                            break;
                        case 'Underneath':
                            s = this.measure(node.children[0], givenSize);
                            w = shapeWidth + s.width + this.options.underneathHorizontalOffset;
                            h = shapeHeight + this.options.underneathVerticalTopOffset + s.height;
                            break;
                        case 'Default':
                            s = this.measure(node.children[0], givenSize);
                            w = shapeWidth + this.options.horizontalSeparation + s.width;
                            h = Math.max(shapeHeight, s.height);
                            break;
                        default:
                            throw new Error('Unhandled TreeDirection in the Radial layout measuring.');
                    }
                    break;
                case 'Up':
                case 'Down':
                    switch (node.childrenLayout) {
                        case 'TopAlignedWithParent':
                        case 'BottomAlignedWithParent':
                            break;
                        case 'Underneath':
                            s = this.measure(node.children[0], givenSize);
                            w = Math.max(shapeWidth, s.width + this.options.underneathHorizontalOffset);
                            h = shapeHeight + this.options.underneathVerticalTopOffset + s.height;
                            break;
                        case 'Default':
                            s = this.measure(node.children[0], givenSize);
                            h = shapeHeight + this.options.verticalSeparation + s.height;
                            w = Math.max(shapeWidth, s.width);
                            break;
                        default:
                            throw new Error('Unhandled TreeDirection in the Down layout measuring.');
                    }
                    break;
                default:
                    throw new Error('Unhandled TreeDirection in the layout measuring.');
            }
            result = new Size(w, h);
        }
        else {
            let i, childNode;
            switch (node.treeDirection) {
                case 'Left':
                case 'Right':
                    switch (node.childrenLayout) {
                        case 'TopAlignedWithParent':
                        case 'BottomAlignedWithParent':
                            break;
                        case 'Underneath':
                            w = shapeWidth;
                            h = shapeHeight + this.options.underneathVerticalTopOffset;
                            for (i = 0; i < node.children.length; i++) {
                                childNode = node.children[i];
                                s = this.measure(childNode, givenSize);
                                w = Math.max(w, s.width + this.options.underneathHorizontalOffset);
                                h += s.height + this.options.underneathVerticalSeparation;
                            }
                            h -= this.options.underneathVerticalSeparation;
                            break;
                        case 'Default':
                            w = shapeWidth;
                            h = 0;
                            for (i = 0; i < node.children.length; i++) {
                                childNode = node.children[i];
                                s = this.measure(childNode, givenSize);
                                w = Math.max(w, shapeWidth + this.options.horizontalSeparation + s.width);
                                h += s.height + this.options.verticalSeparation;
                            }
                            h -= this.options.verticalSeparation;
                            break;
                        default:
                            throw new Error('Unhandled TreeDirection in the Right layout measuring.');
                    }
                    break;
                case 'Up':
                case 'Down':
                    switch (node.childrenLayout) {
                        case 'TopAlignedWithParent':
                        case 'BottomAlignedWithParent':
                            break;
                        case 'Underneath':
                            w = shapeWidth;
                            h = shapeHeight + this.options.underneathVerticalTopOffset;
                            for (i = 0; i < node.children.length; i++) {
                                childNode = node.children[i];
                                s = this.measure(childNode, givenSize);
                                w = Math.max(w, s.width + this.options.underneathHorizontalOffset);
                                h += s.height + this.options.underneathVerticalSeparation;
                            }
                            h -= this.options.underneathVerticalSeparation;
                            break;
                        case 'Default':
                            w = 0;
                            h = 0;
                            for (i = 0; i < node.children.length; i++) {
                                childNode = node.children[i];
                                s = this.measure(childNode, givenSize);
                                w += s.width + this.options.horizontalSeparation;
                                h = Math.max(h, s.height + this.options.verticalSeparation + shapeHeight);
                            }
                            w -= this.options.horizontalSeparation;
                            break;
                        default:
                            throw new Error('Unhandled TreeDirection in the Down layout measuring.');
                    }
                    break;
                default:
                    throw new Error('Unhandled TreeDirection in the layout measuring.');
            }
            result = new Size(w, h);
        }
        node.SectorAngle = Math.sqrt((w * w / 4) + (h * h / 4));
        node.Size = result;
        return result;
    }
    arrange(n, p) {
        const b = n.associatedShape.bounds();
        let i, pp, child, node, childrenwidth;
        const shapeWidth = b.width;
        const shapeHeight = b.height;
        if (isEmpty(n.children)) {
            n.x = p.x;
            n.y = p.y;
            n.BoundingRectangle = new Rect(p.x, p.y, shapeWidth, shapeHeight);
        }
        else {
            let x, y;
            let selfLocation;
            switch (n.treeDirection) {
                case 'Left':
                    switch (n.childrenLayout) {
                        case 'TopAlignedWithParent':
                        case 'BottomAlignedWithParent':
                            break;
                        case 'Underneath':
                            selfLocation = p;
                            n.x = selfLocation.x;
                            n.y = selfLocation.y;
                            n.BoundingRectangle = new Rect(n.x, n.y, n.width, n.height);
                            y = p.y + shapeHeight + this.options.underneathVerticalTopOffset;
                            for (i = 0; i < node.children.length; i++) {
                                node = node.children[i];
                                x = selfLocation.x - node.associatedShape.width - this.options.underneathHorizontalOffset;
                                pp = new Point(x, y);
                                this.arrange(node, pp);
                                y += node.Size.height + this.options.underneathVerticalSeparation;
                            }
                            break;
                        case 'Default':
                            selfLocation = new Point(p.x + n.Size.width - shapeWidth, p.y + ((n.Size.height - shapeHeight) / 2));
                            n.x = selfLocation.x;
                            n.y = selfLocation.y;
                            n.BoundingRectangle = new Rect(n.x, n.y, n.width, n.height);
                            x = selfLocation.x - this.options.horizontalSeparation; // alignment of children
                            y = p.y;
                            for (i = 0; i < n.children.length; i++) {
                                node = n.children[i];
                                pp = new Point(x - node.Size.width, y);
                                this.arrange(node, pp);
                                y += node.Size.height + this.options.verticalSeparation;
                            }
                            break;
                        default:
                            throw new Error('Unsupported TreeDirection');
                    }
                    break;
                case 'Right':
                    switch (n.childrenLayout) {
                        case 'TopAlignedWithParent':
                        case 'BottomAlignedWithParent':
                            break;
                        case 'Underneath':
                            selfLocation = p;
                            n.x = selfLocation.x;
                            n.y = selfLocation.y;
                            n.BoundingRectangle = new Rect(n.x, n.y, n.width, n.height);
                            x = p.x + shapeWidth + this.options.underneathHorizontalOffset;
                            // alignment of children left-underneath the parent
                            y = p.y + shapeHeight + this.options.underneathVerticalTopOffset;
                            for (i = 0; i < n.children.length; i++) {
                                node = n.children[i];
                                pp = new Point(x, y);
                                this.arrange(node, pp);
                                y += node.Size.height + this.options.underneathVerticalSeparation;
                            }
                            break;
                        case 'Default':
                            selfLocation = new Point(p.x, p.y + ((n.Size.height - shapeHeight) / 2));
                            n.x = selfLocation.x;
                            n.y = selfLocation.y;
                            n.BoundingRectangle = new Rect(n.x, n.y, n.width, n.height);
                            x = p.x + shapeWidth + this.options.horizontalSeparation; // alignment of children
                            y = p.y;
                            for (i = 0; i < n.children.length; i++) {
                                node = n.children[i];
                                pp = new Point(x, y);
                                this.arrange(node, pp);
                                y += node.Size.height + this.options.verticalSeparation;
                            }
                            break;
                        default:
                            throw new Error('Unsupported TreeDirection');
                    }
                    break;
                case 'Up':
                    selfLocation = new Point(p.x + ((n.Size.width - shapeWidth) / 2), p.y + n.Size.height - shapeHeight);
                    n.x = selfLocation.x;
                    n.y = selfLocation.y;
                    n.BoundingRectangle = new Rect(n.x, n.y, n.width, n.height);
                    if (Math.abs(selfLocation.x - p.x) < EPSILON) {
                        childrenwidth = 0;
                        // means there is an aberration due to the oversized Element with respect to the children
                        for (i = 0; i < n.children.length; i++) {
                            child = n.children[i];
                            childrenwidth += child.Size.width + this.options.horizontalSeparation;
                        }
                        childrenwidth -= this.options.horizontalSeparation;
                        x = p.x + ((shapeWidth - childrenwidth) / 2);
                    }
                    else {
                        x = p.x;
                    }
                    for (i = 0; i < n.children.length; i++) {
                        node = n.children[i];
                        y = selfLocation.y - this.options.verticalSeparation - node.Size.height;
                        pp = new Point(x, y);
                        this.arrange(node, pp);
                        x += node.Size.width + this.options.horizontalSeparation;
                    }
                    break;
                case 'Down':
                    switch (n.childrenLayout) {
                        case 'TopAlignedWithParent':
                        case 'BottomAlignedWithParent':
                            break;
                        case 'Underneath':
                            selfLocation = p;
                            n.x = selfLocation.x;
                            n.y = selfLocation.y;
                            n.BoundingRectangle = new Rect(n.x, n.y, n.width, n.height);
                            x = p.x + this.options.underneathHorizontalOffset; // alignment of children left-underneath the parent
                            y = p.y + shapeHeight + this.options.underneathVerticalTopOffset;
                            for (i = 0; i < n.children.length; i++) {
                                node = n.children[i];
                                pp = new Point(x, y);
                                this.arrange(node, pp);
                                y += node.Size.height + this.options.underneathVerticalSeparation;
                            }
                            break;
                        case 'Default':
                            selfLocation = new Point(p.x + ((n.Size.width - shapeWidth) / 2), p.y);
                            n.x = selfLocation.x;
                            n.y = selfLocation.y;
                            n.BoundingRectangle = new Rect(n.x, n.y, n.width, n.height);
                            if (Math.abs(selfLocation.x - p.x) < EPSILON) {
                                childrenwidth = 0;
                                // means there is an aberration due to the oversized Element with respect to the children
                                for (i = 0; i < n.children.length; i++) {
                                    child = n.children[i];
                                    childrenwidth += child.Size.width + this.options.horizontalSeparation;
                                }
                                childrenwidth -= this.options.horizontalSeparation;
                                x = p.x + ((shapeWidth - childrenwidth) / 2);
                            }
                            else {
                                x = p.x;
                            }
                            for (i = 0; i < n.children.length; i++) {
                                node = n.children[i];
                                y = selfLocation.y + this.options.verticalSeparation + shapeHeight;
                                pp = new Point(x, y);
                                this.arrange(node, pp);
                                x += node.Size.width + this.options.horizontalSeparation;
                            }
                            break;
                        default:
                            throw new Error('Unsupported TreeDirection');
                    }
                    break;
                case 'None':
                    break;
                default:
                    throw new Error('Unsupported TreeDirection');
            }
        }
    }
    layoutSwitch() {
        if (!this.center) {
            return;
        }
        if (isEmpty(this.center.children)) {
            return;
        }
        let type = this.options.subtype;
        if (isUndefined(type)) {
            type = 'Down';
        }
        let single, male, female, leftcount;
        const children = this.center.children;
        switch (type.toLowerCase()) {
            case 'radial':
            case 'radialtree':
                this.layoutRadialTree();
                break;
            case 'mindmaphorizontal':
            case 'mindmap':
                single = this.center.children;
                if (this.center.children.length === 1) {
                    this.layoutRight(single);
                }
                else {
                    // odd number will give one more at the right
                    leftcount = children.length / 2;
                    male = grep(this.center.children, function (n) {
                        return indexOf(children, n) < leftcount;
                    });
                    female = grep(this.center.children, function (n) {
                        return indexOf(children, n) >= leftcount;
                    });
                    this.layoutLeft(male);
                    this.layoutRight(female);
                }
                break;
            case 'mindmapvertical':
                single = this.center.children;
                if (this.center.children.length === 1) {
                    this.layoutDown(single);
                }
                else {
                    // odd number will give one more at the right
                    leftcount = children.length / 2;
                    male = grep(this.center.children, function (n) {
                        return indexOf(children, n) < leftcount;
                    });
                    female = grep(this.center.children, function (n) {
                        return indexOf(children, n) >= leftcount;
                    });
                    this.layoutUp(male);
                    this.layoutDown(female);
                }
                break;
            case 'right':
                this.layoutRight(this.center.children);
                break;
            case 'left':
                this.layoutLeft(this.center.children);
                break;
            case 'up':
            case 'bottom':
                this.layoutUp(this.center.children);
                break;
            case 'down':
            case 'top':
                this.layoutDown(this.center.children);
                break;
            case 'tipover':
            case 'tipovertree':
                if (this.options.tipOverTreeStartLevel < 0) {
                    throw new Error('The tip-over level should be a positive integer.');
                }
                this.tipOverTree(this.center.children, this.options.tipOverTreeStartLevel);
                break;
        }
    }
}

export { TreeLayoutProcessor };
